// Customs calculator — based on التعريفة الجمركية 2026 + جدول الرسوم
import tariffData from '../data/customsTariff.json';

export interface CalculatorInput {
  weight: number; // tons
  quantity?: number; // pieces when unit is count
  hsCode: string;
  border: string;
  invoiceValue: number; // USD
}

export interface CalculationResult {
  importDutyAmount: number;
  serviceFeeAmount: number;
  tariffTotalAmount: number;
  borderFee: number;
  declarationAdvance: number;
  consumptionTax: number;
  total: number;
  currency: string;
  currencySymbol: string;
  breakdown: {
    weight: number;
    quantity: number;
    unit: string;
    unitLabel: string;
    importDutyRate: number;
    serviceFeeRate: number;
    invoiceValue: number;
    consumptionPercent: number;
    advancePercent: number;
    hsCode?: string;
    hsCodeDescription?: string;
    cargoTypeDescription: string;
    borderDescription: string;
    chapterNote?: string;
  };
}

export type HsInfo = {
  code: string;
  codeDotted?: string;
  description: string;
  consumptionPercent?: number;
  advancePercent?: number;
  unit?: string;
  serviceFee?: number;
  importDuty?: number;
  // legacy fields (ignored if new fields present)
  totalDuty?: number;
  ratePerTon?: number;
};

export type HsSearchResult = HsInfo & {
  chapter?: string;
  chapterNote?: string;
};

const digitsOnly = (code: string): string => code.trim().replace(/[^\d]/g, '');

const normalizeHs = (code: string): string => {
  const digits = digitsOnly(code);
  if (digits.length >= 8) {
    return digits.slice(0, 8);
  }
  return code.trim().replace(/\s+/g, '');
};

const toDotted = (code8: string): string => {
  if (code8.length >= 8) {
    return `${code8.slice(0, 4)}.${code8.slice(4, 6)}.${code8.slice(6, 8)}`;
  }
  return code8;
};

const getHsCodes = (): Record<string, HsInfo> =>
  (tariffData as { hsCodes?: Record<string, HsInfo> }).hsCodes || {};

const getChapterNotes = (): Record<string, string> =>
  (tariffData as { chapterNotes?: Record<string, string> }).chapterNotes || {};

const chapterOf = (code: string): string => digitsOnly(code).slice(0, 2);

export const getChapterNote = (code: string): string | undefined => {
  const chapter = chapterOf(code);
  if (!chapter) return undefined;
  return getChapterNotes()[chapter];
};

export const findHSCode = (code: string): HsInfo | null => {
  if (!code || code.trim() === '') return null;

  const hsCodes = getHsCodes();
  const clean = code.trim().replace(/\s+/g, '');
  if (hsCodes[clean]) return hsCodes[clean];

  const undotted = normalizeHs(clean);
  if (hsCodes[undotted]) return hsCodes[undotted];

  const dotted = toDotted(undotted);
  if (hsCodes[dotted]) return hsCodes[dotted];

  return null;
};

const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[إأآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[^\u0600-\u06FFa-z0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const searchHSCodes = (query: string, limit = 12): HsSearchResult[] => {
  const q = query.trim();
  if (!q || limit <= 0) return [];

  const hsCodes = getHsCodes();
  const chapterNotes = getChapterNotes();
  const entries = Object.values(hsCodes);
  const digitsQuery = digitsOnly(q);
  const isCodeQuery = digitsQuery.length >= 2 && /^[\d.\s-]+$/.test(q);

  const withMeta = (item: HsInfo): HsSearchResult => {
    const chapter = chapterOf(item.code || '');
    return {
      ...item,
      chapter: chapter || undefined,
      chapterNote: chapter ? chapterNotes[chapter] : undefined,
    };
  };

  if (isCodeQuery) {
    const matches = entries
      .filter((item) => {
        const codeDigits = digitsOnly(item.code || '');
        const dotted = item.codeDotted || toDotted(codeDigits);
        return (
          codeDigits.startsWith(digitsQuery) ||
          dotted.replace(/\./g, '').startsWith(digitsQuery) ||
          dotted.startsWith(q.trim())
        );
      })
      .sort((a, b) => digitsOnly(a.code).localeCompare(digitsOnly(b.code)))
      .slice(0, limit)
      .map(withMeta);
    return matches;
  }

  const needle = normalizeSearchText(q);
  if (needle.length < 2) return [];

  const scored: Array<{ item: HsInfo; score: number }> = [];
  for (const item of entries) {
    const chapter = chapterOf(item.code || '');
    const note = chapter ? chapterNotes[chapter] || '' : '';
    const hay = normalizeSearchText(`${item.description || ''} ${note}`);
    if (!hay) continue;

    const idx = hay.indexOf(needle);
    if (idx === -1) continue;

    // Prefer earlier matches and shorter descriptions
    const score = idx * 10 + Math.min(hay.length, 200);
    scored.push({ item, score });
  }

  return scored
    .sort((a, b) => a.score - b.score || digitsOnly(a.item.code).localeCompare(digitsOnly(b.item.code)))
    .slice(0, limit)
    .map(({ item }) => withMeta(item));
};

const unitLabel = (unit?: string): string => {
  switch (unit) {
    case 'count':
      return 'عدد';
    case 'kg':
    case 'weight':
      return 'وزن / كغ';
    case 'percent':
      return '%';
    default:
      return 'وزن / كغ';
  }
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export const calculateCustomsFee = (input: CalculatorInput): CalculationResult | null => {
  const { weight, quantity = 0, hsCode, border, invoiceValue } = input;

  if (!hsCode || hsCode.trim() === '') return null;
  if (!invoiceValue || invoiceValue <= 0) return null;

  const hs = findHSCode(hsCode);
  if (!hs) return null;

  const unit = hs.unit || 'weight';
  const importRate = Number(hs.importDuty ?? 0);
  const serviceRate = Number(hs.serviceFee ?? 0);
  const consumptionPercent = Number(hs.consumptionPercent ?? 0);
  const advancePercent = Number(hs.advancePercent ?? 0);

  let importDutyAmount = 0;
  let serviceFeeAmount = 0;
  let qtyUsed = quantity;

  if (unit === 'count') {
    if (!quantity || quantity <= 0) return null;
    importDutyAmount = quantity * importRate;
    serviceFeeAmount = quantity * serviceRate;
  } else {
    // weight — rates are per kilogram
    if (!weight || weight <= 0) return null;
    const weightKg = weight * 1000;
    qtyUsed = weightKg;
    importDutyAmount = weightKg * importRate;
    serviceFeeAmount = weightKg * serviceRate;
  }

  const tariffTotalAmount = importDutyAmount + serviceFeeAmount;

  const borderKey = border || 'land';
  const borderInfo =
    ((tariffData as any).borderFees || {})[borderKey] ||
    ((tariffData as any).borderFees || {}).land ||
    { additionalFee: 0, description: borderKey };
  const borderFee = Number(borderInfo.additionalFee || 0);

  // سلفة البيان = نسبة البند × قيمة الفاتورة فقط
  const declarationAdvance = invoiceValue * (advancePercent / 100);
  // ضريبة الإنفاق = نسبة البند × قيمة الفاتورة
  const consumptionTax = invoiceValue * (consumptionPercent / 100);

  const total = tariffTotalAmount + borderFee + declarationAdvance + consumptionTax;

  const resolvedCode = hs.code || normalizeHs(hsCode);

  return {
    importDutyAmount: round2(importDutyAmount),
    serviceFeeAmount: round2(serviceFeeAmount),
    tariffTotalAmount: round2(tariffTotalAmount),
    borderFee: round2(borderFee),
    declarationAdvance: round2(declarationAdvance),
    consumptionTax: round2(consumptionTax),
    total: round2(total),
    currency: (tariffData as any).currency || 'USD',
    currencySymbol: (tariffData as any).currencySymbol || '$',
    breakdown: {
      weight,
      quantity: qtyUsed,
      unit,
      unitLabel: unitLabel(unit),
      importDutyRate: importRate,
      serviceFeeRate: serviceRate,
      invoiceValue,
      consumptionPercent,
      advancePercent,
      hsCode: resolvedCode,
      hsCodeDescription: hs.description,
      cargoTypeDescription: hs.description,
      borderDescription: borderInfo.description,
      chapterNote: getChapterNote(resolvedCode),
    },
  };
};

export const getAvailableCargoTypes = () => {
  const rates = (tariffData as any).tariffRates || {};
  return Object.keys(rates).map((key) => ({
    value: key,
    label: rates[key].description,
  }));
};
